import type { Palette } from './types';

type FooterProps = { palette: Palette };

export function HomeProsFooter({ palette }: FooterProps) {
  return (
    <footer 
      className="relative px-6 pb-12 pt-16 text-sm" 
      style={{ backgroundColor: palette.cream }}
    >
      {/* Decorative Top Line */}
      <div 
        className="absolute top-0 left-0 right-0 h-1" 
        style={{ background: `linear-gradient(90deg, ${palette.sage}40, ${palette.forest}40)` }}
      />

      <div className="mx-auto flex max-w-7xl flex-col gap-8">
        
        {/* TOP ROW: Contact & CTA more */}
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between border-b border-slate-200 pb-8">
           <div className="flex flex-col gap-1 text-center sm:text-left">
             <h4 className="text-lg font-bold text-slate-900">Home Pros for Texas</h4>
             <p className="text-slate-500">Simplify your home upgrades.</p>
           </div>

           <div className="flex flex-wrap justify-center gap-6 text-slate-700 sm:justify-end">
            <a href="tel:+12109873429" className="group flex items-center gap-2 font-bold transition hover:text-emerald-700">
               <svg className="h-4 w-4 text-slate-400 group-hover:text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
               (210) 987-3429
            </a>
            <a href="mailto:hello@homepros4tx.com" className="group flex items-center gap-2 font-bold transition hover:text-emerald-700">
               <svg className="h-4 w-4 text-slate-400 group-hover:text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
               hello@homepros4tx.com
            </a>
            <a 
              href="#quote" 
              className="font-bold uppercase tracking-wider transition hover:-translate-y-0.5" 
              style={{ color: palette.clay }}
            >
              Get a Quote →
            </a>
          </div>
        </div>

        {/* BOTTOM ROW: Copyright & Socials */}
        <div className="flex flex-col-reverse items-center justify-between gap-6 sm:flex-row">
          <p className="text-slate-500 text-xs">
            © {new Date().getFullYear()} Home Pros for Texas. All rights reserved.
          </p>

          <div className="flex gap-4">
            {/* Facebook */}
            <a
              href="https://www.facebook.com/homepros4tx"
              target="_top"
              rel="noopener"
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm transition-all hover:bg-blue-50 hover:text-blue-600 hover:-translate-y-1"
            >
              <span className="sr-only">Facebook</span>
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
              </svg>
            </a>

            {/* Instagram */}
            <a
              href="https://www.instagram.com/homepros4texas/"
              target="_top"
              rel="noopener"
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm transition-all hover:bg-pink-50 hover:text-pink-600 hover:-translate-y-1"
            >
              <span className="sr-only">Instagram</span>
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.468 2.37c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
              </svg>
            </a>

            {/* TikTok */}
            <a
              href="https://www.tiktok.com/@homepros4tx"
              target="_top"
              rel="noopener"
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm transition-all hover:bg-slate-50 hover:text-slate-900 hover:-translate-y-1"
            >
              <span className="sr-only">TikTok</span>
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 48 48" aria-hidden="true">
                <path d="M31 6c1 5 4 9 9 10v6c-3 0-6-1-9-3v11c0 8-6 12-12 12S6 38 6 32s4-12 12-12h1v6h-1c-3 0-6 3-6 6s3 6 6 6 6-2 6-6V6h6z" />
              </svg>
            </a>

            {/* LinkedIn */}
            <a
              href="https://www.linkedin.com/company/home-pros-for-texas-llc/"
              target="_top"
              rel="noopener"
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm transition-all hover:bg-blue-50 hover:text-blue-700 hover:-translate-y-1"
            >
               <span className="sr-only">LinkedIn</span>
               <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                 <path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd" />
               </svg>
            </a>
            <a
              href="https://www.youtube.com/channel/UCAG9XSwKUroM6WG4wPljGoA"
              target="_top"
              rel="noopener"
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm transition-all hover:bg-red-50 hover:text-red-600 hover:-translate-y-1"
            >
              <span className="sr-only">YouTube</span>
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1c.5-1.9.5-5.8.5-5.8s0-3.9-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z" />
              </svg>
            </a>
            <a
              href="https://nextdoor.com/page/home-pros-for-texas-llc/"
              target="_top"
              rel="noopener"
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm transition-all hover:bg-green-50 hover:text-green-700 hover:-translate-y-1"
            >
              <span className="sr-only">Nextdoor</span>
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 6.5C4 4.6 5.6 3 7.5 3S11 4.6 11 6.5v2h2v-2C13 4.6 14.6 3 16.5 3S20 4.6 20 6.5V21h-3v-8h-2v8h-3v-8h-2v8H7V6.5a.5.5 0 0 0-1 0V21H4V6.5z" />
              </svg>
            </a>
            <a href="/home-pros/privacy" target="_top" rel="noopener" className="text-xs font-semibold text-slate-600 hover:text-slate-900">Privacy</a>
            <a href="/home-pros/terms" target="_top" rel="noopener" className="text-xs font-semibold text-slate-600 hover:text-slate-900">Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
